// Import Dependencies
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import dayjs from "dayjs";
import {
  Dialog,
  DialogPanel,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import {
  XMarkIcon,
  CalendarIcon,
  ChatBubbleLeftRightIcon,
  PhoneIcon,
  EnvelopeIcon,
  CalendarDaysIcon,
  DevicePhoneMobileIcon,
  VideoCameraIcon,
  ArrowPathIcon,
  UserCircleIcon,
  FlagIcon,
  ChartBarIcon,
  PlusIcon,
  EyeIcon,
  FireIcon,
  PaperAirplaneIcon,
  InformationCircleIcon,
  ClockIcon,
} from "@heroicons/react/24/outline";

// Local Imports
import { Button, ScrollShadow, Card, Badge, Progress } from "components/ui";
import { useDisclosure } from "hooks";
import { useLocaleContext } from "app/contexts/locale/context";
import { getCommunicationTimeline, getCommunicationAndTelephonyStatus } from "services/leadsService";
import { getIntegrationProviders } from "services/integrationService";
import { toast } from "sonner";
import VerticalSliderIcon from "assets/dualicons/vertical-slider.svg?react";
import { hasPermission } from "utils/permissionsUtils";
import { formatDate, formatTime, getLocalDateTime } from "../../../utils/dateUtils";

// ----------------------------------------------------------------------

export function LeadRightSidebar({
  leadData,
  setIsFollowUpOpen,
  showRightPanel,
  setShowRightPanel,
  handleStageChange,
  handleOwnerChange,
  handlePriorityChange,
  onLeadsImported,
  stageConfirmModal,
  isMobile,
  showStageModal,
  setShowStageModal,
  onLeadDataRefresh, // New prop for refreshing lead data after follow-up creation
  // Modal setters
  setShowOwnerModal,
  setShowPriorityModal,
  setShowAnalyticsModal,
  setShowWhatsAppModal,
  setShowEmailModal,
  setShowSMSModal,
  setShowFollowUpModal,
  setShowCallModal,
  setShowInteractionLevelModal,
}) {
  const [isOpen, { open, close }] = useDisclosure();

  // Sync with external show/hide state
  const handleClose = () => {
    if (stageConfirmModal.show) {
      return;
    }
    close();
    if (setShowRightPanel) {
      setShowRightPanel(false);
    }
  };

  // Open sidebar when showRightPanel becomes true
  React.useEffect(() => {
    if (showRightPanel && !isOpen) {
      open();
    }
  }, [showRightPanel, isOpen, open]);

  // Close external state when sidebar closes
  React.useEffect(() => {
    if (!isOpen && setShowRightPanel) {
      setShowRightPanel(false);
    }
  }, [isOpen, setShowRightPanel]);

  return (
    <>
      <Button
        onClick={() => {
          open();
          if (setShowRightPanel) setShowRightPanel(true);
        }}
        variant="flat"
        isIcon
        className="relative size-9 rounded-full"
      >
        <VerticalSliderIcon className="size-6" />
      </Button>
      <LeadRightSidebarContent
        isOpen={isOpen}
        close={handleClose}
        leadData={leadData}
        isMobile={isMobile}
        stageConfirmModal={stageConfirmModal}
        handleStageChange={handleStageChange}
        handleOwnerChange={handleOwnerChange}
        handlePriorityChange={handlePriorityChange}
        onLeadsImported={onLeadsImported}
        setIsFollowUpOpen={setIsFollowUpOpen}
        onLeadDataRefresh={onLeadDataRefresh}
        showStageModal={showStageModal}
        setShowStageModal={setShowStageModal}
        // Modal setters
        setShowOwnerModal={setShowOwnerModal}
        setShowPriorityModal={setShowPriorityModal}
        setShowAnalyticsModal={setShowAnalyticsModal}
        setShowWhatsAppModal={setShowWhatsAppModal}
        setShowEmailModal={setShowEmailModal}
        setShowSMSModal={setShowSMSModal}
        setShowFollowUpModal={setShowFollowUpModal}
        setShowCallModal={setShowCallModal}
        setShowInteractionLevelModal={setShowInteractionLevelModal}
      />
    </>
  );
}

function LeadRightSidebarContent({
  isOpen,
  close,
  leadData,
  setIsFollowUpOpen,
  isMobile,
  handleStageChange,
  handleOwnerChange,
  handlePriorityChange,
  onLeadsImported,
  stageConfirmModal,
  onLeadDataRefresh,
  showStageModal,
  setShowStageModal,
  // Modal setters
  setShowOwnerModal,
  setShowPriorityModal,
  setShowAnalyticsModal,
  setShowWhatsAppModal,
  setShowEmailModal,
  setShowSMSModal,
  setShowFollowUpModal,
  setShowCallModal,
  setShowInteractionLevelModal,
}) {
  const { locale } = useLocaleContext();


  // State for recent communications
  const [recentCommunications, setRecentCommunications] = useState([]);
  const [communicationsLoading, setCommunicationsLoading] = useState(false);
  const [communicationsError, setCommunicationsError] = useState(null);

  // State for communication and telephony status
  const [communicationStatus, setCommunicationStatus] = useState({
    emailSent: 0,
    smsSent: 0,
    loading: false,
    error: null,
  });

  const [telephonyStatus, setTelephonyStatus] = useState({
    inboundCall: 0,
    outboundCall: 0,
    loading: false,
    error: null,
  });

  const [integrationAvailability, setIntegrationAvailability] = useState({
    whatsapp: false,
    twilio: false,
    smtp: false,
    sendgrid: false,
  });
  // const [hasAnnouncedIntegrationInfo, setHasAnnouncedIntegrationInfo] = useState(false);

  // Function to fetch recent communications
  const fetchRecentCommunications = async () => {
    // Check for different possible ID field names in leadData
    const leadId = leadData?.id || leadData?.leadId || leadData?.LeadId;

    if (!leadId) {

      return;
    }

    setCommunicationsLoading(true);
    setCommunicationsError(null);

    try {
      const response = await getCommunicationTimeline(
        leadId,
        null, // All channels (matches CommunicationsTab pattern exactly)
        1, // Page 1
        2  // Get only 2 latest communications
      );


      if (response.success) {
        // Sort by date (newest first) and take only the first 2
        const sortedCommunications = response.data
          .sort((a, b) => getLocalDateTime(b.SentAt || b.CreatedAt) - getLocalDateTime(a.SentAt || a.CreatedAt))
          .slice(0, 2);
        setRecentCommunications(sortedCommunications);
      } else {
        setCommunicationsError(response.error || "Failed to fetch communications");
      }
    } catch (error) {
      console.error("LeadRightSidebar: API Exception:", error);
      setCommunicationsError("Failed to fetch recent communications");
    } finally {
      setCommunicationsLoading(false);
    }
  };

  // Fetch recent communications when component mounts or leadData changes
  useEffect(() => {
    const leadId = leadData?.id || leadData?.leadId || leadData?.LeadId;
    fetchRecentCommunications();
  }, [leadData?.id, leadData?.leadId, leadData?.LeadId]);

  // Function to fetch communication and telephony status
  const fetchCommunicationAndTelephonyStatus = async () => {
    const leadId = leadData?.id || leadData?.leadId || leadData?.LeadId;

    if (!leadId) {
      return;
    }

    // Set loading states
    setCommunicationStatus(prev => ({ ...prev, loading: true, error: null }));
    setTelephonyStatus(prev => ({ ...prev, loading: true, error: null }));

    try {
      const response = await getCommunicationAndTelephonyStatus(leadId);

      if (response.success && response.data) {
        const statusData = response.data;

        // Set communication status
        setCommunicationStatus({
          emailSent: statusData.emailSent || statusData.EmailSent || 0,
          smsSent: statusData.smsSent || statusData.SMSSent || 0,
          loading: false,
          error: null,
        });

        // Set telephony status
        setTelephonyStatus({
          inboundCall: statusData.inboundCall || statusData.InBoundCalls || 0,
          outboundCall: statusData.outboundCall || statusData.OutBoundCalls || 0,
          loading: false,
          error: null,
        });
      } else {
        throw new Error(response.error || "Failed to fetch status");
      }
    } catch (error) {
      console.error("Error fetching communication and telephony status:", error);

      setCommunicationStatus(prev => ({
        ...prev,
        loading: false,
        error: error.message,
      }));
      setTelephonyStatus(prev => ({
        ...prev,
        loading: false,
        error: error.message,
      }));
    }
  };

  // Fetch communication and telephony status when component mounts or leadData changes
  useEffect(() => {
    fetchCommunicationAndTelephonyStatus();
  }, [leadData?.id, leadData?.leadId, leadData?.LeadId]);

  useEffect(() => {
    let isMounted = true;

    const fetchIntegrationProviders = async () => {
      try {
        const response = await getIntegrationProviders();
        const availability = {
          whatsapp: false,
          twilio: false,
          sendgrid: false,
          smtp: false,
        };

        if (response?.success && Array.isArray(response.data)) {
          response.data.forEach((provider) => {
            const normalizedName = String(
              provider?.Name || provider?.name || provider?.Code || provider?.code || "",
            )
              .trim()
              .toLowerCase();
            const isActive = provider?.IsActive ?? provider?.isActive;
            const activeFlag = isActive === true;

            if (normalizedName.includes("whatsapp")) {
              availability.whatsapp = activeFlag;
            }

            if (normalizedName.includes("twilio")) {
              availability.twilio = activeFlag;
            }

            if (normalizedName.includes("sendgrid")) {
              availability.sendgrid = activeFlag;
            }
            if (normalizedName.includes("smtp")) {
              availability.smtp = activeFlag;
            }
          });
        }

        if (isMounted) {
          setIntegrationAvailability(availability);
        }
      } catch (error) {
        console.error("Error loading integration providers:", error);
        if (isMounted) {
          setIntegrationAvailability({
            whatsapp: false,
            twilio: false,
            sendgrid: false,
          });
        }
      }
    };

    fetchIntegrationProviders();

    return () => {
      isMounted = false;
    };
  }, []);

  // useEffect(() => {
  //   if (hasAnnouncedIntegrationInfo) return;
  //   const anyInactive = !integrationAvailability.whatsapp || !integrationAvailability.twilio || !integrationAvailability.sendgrid;
  //   if (anyInactive) {
  //     toast.info("Connect WhatsApp, Twilio, or SendGrid integrations in Settings to enable all quick actions.");
  //     setHasAnnouncedIntegrationInfo(true);
  //   }
  // }, [integrationAvailability, hasAnnouncedIntegrationInfo]);

  const handleIntegrationAction = (isEnabled, label, callback) => {
    if (!isEnabled) {
      toast.info(`${label} integration is disabled. Enable it under Settings → Integrations to use this action.`);
      return;
    }
    callback?.();
  };

  // Helper function to get channel icon and color
  const getChannelStyle = (channelId) => {
    switch (channelId) {
      case 1: // EMAIL
        return { icon: EnvelopeIcon, color: "primary", label: "Email" };
      case 2: // SMS
        return { icon: PaperAirplaneIcon, color: "info", label: "SMS" };
      case 3: // WHATSAPP
        return {
          icon: ChatBubbleLeftRightIcon,
          color: "success",
          label: "WhatsApp",
        };
      case 4: // CALL
        return {
          icon: PhoneIcon,
          color: "indigo",
          label: "Call",
        };
      default:
        return {
          icon: InformationCircleIcon,
          color: "neutral",
          label: "Unknown",
        };
    }
  };

  // Helper function to format relative time
  const formatRelativeTime = (dateString) => {
    try {
      const date = getLocalDateTime(dateString);
      const now = new Date();
      const diffTime = Math.abs(now - date);
      const diffMinutes = Math.floor(diffTime / (1000 * 60));
      const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

      if (diffMinutes < 60) {
        return `${diffMinutes}m ago`;
      } else if (diffHours < 24) {
        return `${diffHours}h ago`;
      } else if (diffDays < 7) {
        return `${diffDays}d ago`;
      } else {
        return formatNotificationTime(dateString);
      }
    } catch (error) {
      return "Unknown time";
    }
  };
  // Helper function to get the next upcoming follow-up
  const getNextFollowUp = () => {
    if (
      !leadData ||
      !leadData.upcomingFollowUps ||
      leadData.upcomingFollowUps.length === 0
    ) {
      return null;
    }

    // Filter pending follow-ups and sort by date
    const pendingFollowUps = leadData.upcomingFollowUps
      .filter(
        (followUp) =>
          followUp.Status === 1 || followUp.StatusName === "Pending",
      )
      .sort((a, b) => getLocalDateTime(a.ScheduledDate) - getLocalDateTime(b.ScheduledDate));

    return pendingFollowUps.length > 0 ? pendingFollowUps[0] : null;
  };

  const nextFollowUp = getNextFollowUp();

  return (
    <Transition show={isOpen}>
      <Dialog
        open={true}
        onClose={() => { }}
        static
        autoFocus
      >
        <TransitionChild
          as="div"
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
          className="fixed inset-0 z-60 bg-gray-900/50 backdrop-blur-sm transition-opacity dark:bg-black/40"
        ></TransitionChild>

        <TransitionChild
          as={DialogPanel}
          data-sidebar="true"
          enter="ease-out transform-gpu transition-transform duration-200"
          enterFrom="translate-x-full"
          enterTo="translate-x-0"
          leave="ease-in transform-gpu transition-transform duration-200"
          leaveFrom="translate-x-0"
          leaveTo="translate-x-full"
          className={`dark:bg-dark-750 fixed inset-y-0 right-0 z-61 flex transform-gpu flex-col bg-white transition-transform duration-200 ${isMobile
            ? "w-screen"
            : "w-screen sm:inset-y-2 sm:mx-2 sm:w-80 sm:rounded-xl"
            }`}
        >
          {/* Header */}
          <div
            className={`flex items-center justify-between ${isMobile ? "px-3 py-2" : "px-4 py-3"} dark:border-dark-600 border-b border-gray-200`}
          >
            <div className="flex items-center gap-2">
              <CalendarIcon className="dark:text-dark-400 size-4 text-gray-500" />
              <div>
                <span
                  className={`${isMobile ? "text-xs" : "text-sm"} dark:text-dark-50 font-medium text-gray-900`}
                >
                  Lead Actions
                </span>
                <p
                  className={`${isMobile ? "text-xs" : "text-xs"} dark:text-dark-400 text-gray-500`}
                >
                  {/* {now} */}
                </p>
              </div>
            </div>
            <Button
              onClick={close}
              variant="flat"
              isIcon
              className="size-6 rounded-full"
            >
              <XMarkIcon className="size-4" />
            </Button>
          </div>

          <ScrollShadow
            size={4}
            className="hide-scrollbar flex-1 overflow-y-auto overscroll-contain"
          >
            {/* Quick Actions Section */}
            <div
              className={`${isMobile ? "p-3" : "p-4"} dark:border-dark-600 border-b border-gray-200`}
            >
              <h4
                className={`${isMobile ? "text-xs" : "text-sm"} dark:text-dark-50 mb-3 font-semibold text-gray-900`}
              >
                Quick Actions
              </h4>
              {!hasPermission("FUPSCFUP") && !hasPermission("COMMWTSP") && !hasPermission("COMMCL") && !hasPermission("COMMEML") && !hasPermission("COMMSMS") ?
                <span className="flex h-auto flex-col items-center p-3">
                  <span className="text-xs">No actions available</span>
                </span>
                : null}
              <div
                className={`grid ${isMobile ? "grid-cols-2 gap-2" : "grid-cols-3 gap-2"}`}
              >
                {hasPermission("COMMWTSP") ? <Button
                  variant="soft"
                  color="success"
                  disabled={leadData?.IsStudent}
                  className={`flex flex-col items-center ${isMobile ? "p-2" : "p-3"} h-auto ${leadData?.IsStudent ? "opacity-60 cursor-not-allowed" : ""}`}
                  onClick={() => {
                    setShowWhatsAppModal(true);
                    close();
                  }}
                >
                  <ChatBubbleLeftRightIcon
                    className={`${isMobile ? "h-4 w-4" : "h-5 w-5"} mb-1`}
                  />
                  <span className="text-xs">WhatsApp</span>
                </Button> : null}
                {hasPermission("COMMCL") ? <Button
                  variant="soft"
                  color="primary"
                  disabled={leadData?.IsStudent}
                  className={`flex flex-col items-center ${isMobile ? "p-2" : "p-3"} h-auto ${integrationAvailability.twilio && !leadData?.IsStudent ? "" : "cursor-not-allowed opacity-60"
                    }`}
                  onClick={() => {
                    handleIntegrationAction(integrationAvailability.twilio, "Twilio", () => {
                      setShowCallModal(true);
                      close();
                    });
                  }}
                >
                  <PhoneIcon
                    className={`${isMobile ? "h-4 w-4" : "h-5 w-5"} mb-1`}
                  />
                  <span className="text-xs">Call</span>
                </Button> : null}
                {hasPermission("COMMEML") ? <Button
                  variant="soft"
                  color="info"
                  disabled={leadData?.IsStudent}
                  className={`flex h-auto flex-col items-center p-3 ${integrationAvailability.smtp && !leadData?.IsStudent ? "" : "cursor-not-allowed opacity-60"
                    }`}
                  onClick={() => {
                    handleIntegrationAction(integrationAvailability.smtp, "SMTP", () => {
                      setShowEmailModal(true);
                      close();
                    });
                  }}
                >
                  <EnvelopeIcon className="mb-1 h-5 w-5" />
                  <span className="text-xs">Email</span>
                </Button> : null}
                {hasPermission("FUPSCFUP") ? <Button
                  variant="soft"
                  color="warning"
                  disabled={leadData?.IsStudent}
                  className={`flex h-auto flex-col items-center p-3 ${leadData?.IsStudent ? "opacity-60 cursor-not-allowed" : ""}`}
                  onClick={() => {
                    setShowFollowUpModal(true);
                    close();
                  }}
                >
                  <CalendarDaysIcon className="mb-1 h-5 w-5" />
                  <span className="text-xs">Follow-up</span>
                </Button> : null}
                {hasPermission("COMMSMS") ? <Button
                  variant="soft"
                  color="neutral"
                  disabled={leadData?.IsStudent}
                  className={`flex h-auto flex-col items-center p-3 ${integrationAvailability.twilio && !leadData?.IsStudent ? "" : "cursor-not-allowed opacity-60"
                    }`}
                  onClick={() => {
                    handleIntegrationAction(integrationAvailability.twilio, "Twilio", () => {
                      setShowSMSModal(true);
                      close();
                    });
                  }}
                >
                  <DevicePhoneMobileIcon className="mb-1 h-5 w-5" />
                  <span className="text-xs">SMS</span>
                </Button> : null}




                {/* <Button
                  variant="soft"
                  color="primary"
                  className="flex h-auto flex-col items-center p-3"
                >
                  <VideoCameraIcon className="mb-1 h-5 w-5" />
                  <span className="text-xs">Video Call</span>
                </Button> */}
              </div>
            </div>

            {/* Lead Management Section */}
            <div
              className={`${isMobile ? "p-3" : "p-4"} dark:border-dark-600 border-b border-gray-200`}
            >
              <h4
                className={`${isMobile ? "text-xs" : "text-sm"} dark:text-dark-50 mb-3 font-semibold text-gray-900`}
              >
                Lead Management
              </h4>
              <div className="space-y-2">
                {hasPermission("MLLE") ? <Button
                  variant="outlined"
                  color="primary"
                  disabled={leadData?.IsStudent}
                  className={`w-full justify-start text-sm ${leadData?.IsStudent ? "opacity-60 cursor-not-allowed" : ""}`}
                  onClick={() => {
                    setShowStageModal(true);
                    close();
                  }}
                >
                  <ArrowPathIcon className="mr-2 h-4 w-4" />
                  Change Stage
                </Button> : null}
                {hasPermission("MLASGN") ? <Button
                  variant="outlined"
                  color="neutral"
                  disabled={leadData?.IsStudent}
                  className={`w-full justify-start text-sm ${leadData?.IsStudent ? "opacity-60 cursor-not-allowed" : ""}`}
                  onClick={() => {
                    setShowOwnerModal(true);
                    close();
                  }}
                >
                  <UserCircleIcon className="mr-2 h-4 w-4" />
                  Assign Owner
                </Button> : null}
                {hasPermission("MLLE") ? <Button
                  variant="outlined"
                  color="warning"
                  disabled={leadData?.IsStudent}
                  className={`w-full justify-start text-sm ${leadData?.IsStudent ? "opacity-60 cursor-not-allowed" : ""}`}
                  onClick={() => {
                    setShowPriorityModal(true);
                    close();
                  }}
                >
                  <FlagIcon className="mr-2 h-4 w-4" />
                  Set Priority
                </Button> : null}
                {hasPermission("MLLE") ? <Button
                  variant="outlined"
                  color="neutral"
                  disabled={leadData?.IsStudent}
                  className={`w-full justify-start text-sm ${leadData?.IsStudent ? "opacity-60 cursor-not-allowed" : ""}`}
                  onClick={() => {
                    setShowInteractionLevelModal(true);
                    close();
                  }}
                >
                  <FireIcon className="mr-2 h-4 w-4" />
                  Set Interaction Level
                </Button> : null}
                <Button
                  variant="outlined"
                  color="info"
                  className="w-full justify-start text-sm"
                  onClick={() => {
                    setShowAnalyticsModal(true);
                    close();
                  }}
                >
                  <ChartBarIcon className="mr-2 h-4 w-4" />
                  Lead Analytics
                </Button>
              </div>
            </div>

            {/* Communication History */}
            <div className="dark:border-dark-600 border-b border-gray-200 p-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="dark:text-dark-50 text-sm font-semibold text-gray-900">
                  Recent Communications
                </h4>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={fetchRecentCommunications}
                  disabled={communicationsLoading}
                  className="h-6 w-6 p-0"
                >
                  <ArrowPathIcon
                    className={`h-3 w-3 ${communicationsLoading ? "animate-spin" : ""}`}
                  />
                </Button>
              </div>
              <div className="space-y-2">
                {communicationsLoading ? (
                  <div className="flex items-center justify-center py-4">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                    <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                      Loading...
                    </span>
                  </div>
                ) : communicationsError ? (
                  <div className="text-center py-2">
                    <p className="text-xs text-red-500 dark:text-red-400 mb-2">
                      {communicationsError}
                    </p>
                    <Button
                      variant="outlined"
                      size="sm"
                      onClick={fetchRecentCommunications}
                      className="text-xs"
                    >
                      Retry
                    </Button>
                  </div>
                ) : recentCommunications.length === 0 ? (
                  <div className="text-center py-4">
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      No recent communications
                    </p>
                  </div>
                ) : (
                  recentCommunications.map((communication) => {
                    const { icon: ChannelIcon, color: channelColor, label: channelLabel } = getChannelStyle(communication.ChannelId);

                    return (
                      <div key={communication.LogId} className="dark:bg-dark-700 flex items-center gap-3 rounded-lg bg-gray-50 p-2">
                        <div className={`bg-${channelColor}-100 dark:bg-${channelColor}-900/20 flex h-6 w-6 items-center justify-center rounded-full`}>
                          <ChannelIcon className={`text-${channelColor}-600 dark:text-${channelColor}-400 h-3 w-3`} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="dark:text-dark-50 truncate text-xs font-medium text-gray-900">
                            {communication.ChannelName}
                          </p>
                          <p className="dark:text-dark-400 truncate text-xs text-gray-500">
                            {communication.Recipient ?
                              (communication.Recipient.length > 30 ?
                                `${communication.Recipient.substring(0, 30)}...` :
                                communication.Recipient) :
                              `${channelLabel} communication`}
                          </p>
                        </div>
                        <span className="dark:text-dark-500 shrink-0 text-xs text-gray-400">
                          {formatRelativeTime(communication.SentAt || communication.CreatedAt)}
                        </span>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* Lead Insights */}
            <div className="dark:border-dark-600 border-b border-gray-200 p-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="dark:text-dark-50 text-sm font-semibold text-gray-900">
                  Lead Insights
                </h4>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    fetchCommunicationAndTelephonyStatus();
                    fetchRecentCommunications();
                  }}
                  disabled={communicationStatus.loading || telephonyStatus.loading}
                  className="h-6 w-6 p-0"
                >
                  <ArrowPathIcon
                    className={`h-3 w-3 ${(communicationStatus.loading || telephonyStatus.loading) ? "animate-spin" : ""}`}
                  />
                </Button>
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="dark:text-dark-300 text-sm text-gray-600">
                    Lead Score
                  </span>
                  <Badge
                    variant="soft"
                    color="primary"
                    className="text-xs font-semibold"
                  >
                    {leadData?.leadScore === "Unscored"
                      ? "Unscored"
                      : `${leadData?.leadScore || 0}/100`}
                  </Badge>
                </div>
                <Progress
                  value={
                    leadData?.leadScore === "Unscored"
                      ? 0
                      : leadData?.leadScore || 0
                  }
                  color="primary"
                  className="h-2"
                />
                <div className="grid grid-cols-2 gap-3 text-center">
                  <div className="dark:bg-dark-700 rounded-lg bg-gray-50 p-2">
                    <div className="dark:text-dark-50 text-lg font-semibold text-gray-900">
                      {communicationStatus.loading || telephonyStatus.loading
                        ? "..."
                        : (telephonyStatus.inboundCall + telephonyStatus.outboundCall + communicationStatus.emailSent + communicationStatus.smsSent)}
                    </div>
                    <div className="dark:text-dark-400 text-xs text-gray-500">
                      Interactions
                    </div>
                  </div>
                  <div className="dark:bg-dark-700 rounded-lg bg-gray-50 p-2">
                    <div className="dark:text-dark-50 text-lg font-semibold text-gray-900">
                      {communicationStatus.loading
                        ? "..."
                        : communicationStatus.emailSent}
                    </div>
                    <div className="dark:text-dark-400 text-xs text-gray-500">
                      Emails Sent
                    </div>
                  </div>
                  <div className="dark:bg-dark-700 rounded-lg bg-gray-50 p-2">
                    <div className="dark:text-dark-50 text-lg font-semibold text-gray-900">
                      {telephonyStatus.loading
                        ? "..."
                        : (telephonyStatus.inboundCall + telephonyStatus.outboundCall)}
                    </div>
                    <div className="dark:text-dark-400 text-xs text-gray-500">
                      Calls
                    </div>
                  </div>
                  <div className="dark:bg-dark-700 rounded-lg bg-gray-50 p-2">
                    <div className="dark:text-dark-50 text-lg font-semibold text-gray-900">
                      {communicationStatus.loading
                        ? "..."
                        : communicationStatus.smsSent}
                    </div>
                    <div className="dark:text-dark-400 text-xs text-gray-500">
                      SMS Sent
                    </div>
                  </div>
                </div>
                {(communicationStatus.error || telephonyStatus.error) && (
                  <div className="text-center py-2">
                    <p className="text-xs text-red-500 dark:text-red-400 mb-2">
                      Failed to load insights data
                    </p>
                    <Button
                      variant="outlined"
                      size="sm"
                      onClick={() => {
                        fetchCommunicationAndTelephonyStatus();
                        fetchRecentCommunications();
                      }}
                      className="text-xs"
                    >
                      Retry
                    </Button>
                  </div>
                )}
              </div>
            </div>

            {/* Upcoming Follow-ups */}
            <div className="p-4">
              <h4 className="dark:text-dark-50 mb-3 text-sm font-semibold text-gray-900">
                Upcoming Follow-ups
              </h4>
              {nextFollowUp ? (
                <Card className="bg-warning-50 dark:bg-warning-900/10 border-warning-200 dark:border-warning-800 p-3">
                  <div className="flex items-start gap-3">
                    <CalendarDaysIcon className="text-warning-600 mt-1 dark:text-warning-400 h-4 w-4 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="dark:text-dark-50 text-sm font-medium text-gray-900">
                        {nextFollowUp.FollowUpType || nextFollowUp.Type || "Follow-up"}
                      </p>
                      <div className="flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 mb-1">
                        <CalendarDaysIcon className="h-3 w-3" />
                        <span className="font-medium">
                          {formatDate(nextFollowUp.ScheduledDate)}
                        </span>
                        {nextFollowUp.ScheduledTime && (
                          <>
                            <ClockIcon className="ml-1 h-3 w-3" />
                            <span>{formatTime(nextFollowUp.ScheduledTime)}</span>
                          </>
                        )}
                      </div>
                      <div className="flex items-center justify-between mb-1">
                        <span className="dark:text-dark-400 text-xs text-gray-500">
                          {nextFollowUp.AssignedTo || "Unassigned"}
                        </span>
                        <Badge
                          variant="soft"
                          color={
                            nextFollowUp.Priority === "high"
                              ? "error"
                              : nextFollowUp.Priority === "medium"
                                ? "warning"
                                : "neutral"
                          }
                          className="text-xs"
                        >
                          {nextFollowUp.Priority || "normal"}
                        </Badge>
                      </div>
                      {nextFollowUp.Remarks && (
                        <p className="dark:text-dark-500 truncate text-xs text-gray-400 italic">
                          &ldquo;{nextFollowUp.Remarks}&rdquo;
                        </p>
                      )}
                    </div>
                    {/* <Button
                      variant="soft"
                      color="warning"
                      className="shrink-0 px-2 py-1 text-xs"
                    >
                      Reschedule
                    </Button> */}
                  </div>
                </Card>
              ) : (
                <Card className="bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 p-3">
                  <div className="flex items-center gap-3">
                    <CalendarDaysIcon className="text-gray-400 dark:text-gray-500 h-4 w-4 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="dark:text-dark-50 text-sm font-medium text-gray-900">
                        No follow-up scheduled
                      </p>
                      <p className="dark:text-dark-400 truncate text-xs text-gray-500">
                        Schedule a follow-up to stay on track
                      </p>
                    </div>
                  </div>
                </Card>
              )}
              {hasPermission("FUPSCFUP") ? <Button
                variant="outlined"
                color="primary"
                className="mt-3 w-full text-sm"
                onClick={() => {
                  setShowFollowUpModal(true);
                  close();
                }}
              >
                <PlusIcon className="mr-2 h-4 w-4" />
                Schedule Follow-up
              </Button> : null}
            </div>
          </ScrollShadow>
        </TransitionChild>
      </Dialog>
    </Transition>
  );
}

LeadRightSidebar.propTypes = {
  leadData: PropTypes.object,
  setIsFollowUpOpen: PropTypes.func,
  showRightPanel: PropTypes.bool,
  setShowRightPanel: PropTypes.func,
  isMobile: PropTypes.bool,
  handleStageChange: PropTypes.func,
  handleOwnerChange: PropTypes.func,
  handlePriorityChange: PropTypes.func,
  onLeadsImported: PropTypes.func,
  stageConfirmModal: PropTypes.object,
  // Modal setters
  setShowOwnerModal: PropTypes.func,
  setShowPriorityModal: PropTypes.func,
  setShowAnalyticsModal: PropTypes.func,
  setShowWhatsAppModal: PropTypes.func,
  setShowEmailModal: PropTypes.func,
  setShowSMSModal: PropTypes.func,
  setShowFollowUpModal: PropTypes.func,
  setShowCallModal: PropTypes.func,
};

LeadRightSidebarContent.propTypes = {
  isOpen: PropTypes.bool,
  close: PropTypes.func,
  leadData: PropTypes.object,
  setIsFollowUpOpen: PropTypes.func,
  isMobile: PropTypes.bool,
  handleStageChange: PropTypes.func,
  handleOwnerChange: PropTypes.func,
  handlePriorityChange: PropTypes.func,
  onLeadsImported: PropTypes.func,
  stageConfirmModal: PropTypes.object,
  // Modal setters
  setShowOwnerModal: PropTypes.func,
  setShowPriorityModal: PropTypes.func,
  setShowAnalyticsModal: PropTypes.func,
  setShowWhatsAppModal: PropTypes.func,
  setShowEmailModal: PropTypes.func,
  setShowSMSModal: PropTypes.func,
  setShowFollowUpModal: PropTypes.func,
  setShowCallModal: PropTypes.func,
};

export default LeadRightSidebar;
